import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Quote from "../Quote/quote";
import List from "../List/list";
import Embed from "../Embed/embed";
import Table from "../Table/table";
import Imaegs from "../Image/image";
import Paragraph from "../Paragraph/paragraph";
import Select from "react-select";
import { typeOptions } from "pages/Blog/constants/blog";
import {
  ChevronDown,
  FileEdit,
  GripVertical,
  Loader2,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import Video from "../Video/video";
import { SubSection, useFormContext } from "context/BlogContext";
import { Section } from "pages/Blog/AddBlogForm/createBlog";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { Dropdown } from "Common/Components/Dropdown";
import DeleteModal from "Common/DeleteModal";
import { Tooltip } from "react-tooltip";
import Code from "../Code/code";
import { createSelector } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Modal from "Common/Components/Modal";
import { generateUniqueId } from "utils/uuid";
import Product from "../Product/product";
import { title } from "process";
import { getJewelleryBySlug } from "slices/thunk";

interface SectionInfoProps {
  section: Section;
}

type BlogStatus = "draft" | "published" | "archived";

const SectionInfo: React.FC<SectionInfoProps> = ({ section }) => {
  const {
    blogValidation,
    getNextOrder,
    setBlogInfo,
    blogInfo,
    handleSaveSection,
    setSelectedSectionOrder,
    handlePublishBlog,
    isBlogEditing,
    deletedSubSectionIds,
    setDeletedSubSectionIds,
    deletedSectionIds,
    setDeletedSectionIds,
  } = useFormContext();
  const [selectedType, setSelectedType] = useState<string>("");
  const [deleteSection, setDeleteSection] = useState<boolean>(false);
  const [deleteSubSection, setDeleteSubSection] = useState<boolean>(false);
  const [defaultModal, setDefaultModal] = useState<boolean>(false);
  const [orderUpdated, setOrderUpdated] = useState<boolean>(false);
  const [isProductSubSection, setIsProductSubSection] =
    useState<boolean>(false);

  const navigate = useNavigate();

  const selectDataList = createSelector(
    (state: any) => state.Blog,
    (state) => ({
      isLoading: state.isLoading,
    })
  );

  const { isLoading } = useSelector(selectDataList);

  const validation: any = useFormik<Section>({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,
    initialValues: {
      title: section?.title || "",
      body: section?.body || [],
      order: section?.order || 1,
      isEditing: false,
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Section title is required"),
      body: Yup.array().of(
        Yup.object({
          type: Yup.string().required("Type is required"),
          attrs: Yup.object().required("Attributes are required"),
        })
      ),
    }),

    onSubmit: (values) => {
      handleSaveSection(values as Section);
      setOrderUpdated(false);
    },
  });

  const defaultToggle = () => setDefaultModal(!defaultModal);

  const handleTypeChange = (
    option: { value: string; label: string } | null,
    subSectionOrder: number
  ) => {
    const clonedSubSections = [...validation.values.body];

    const clonedBlogInfo = { ...blogInfo };

    const sectionIndex = clonedBlogInfo.sections.findIndex(
      (section_) => section_.order === section.order
    );

    const subSectionIndex = clonedSubSections.findIndex(
      (subSection) => subSection.order === subSectionOrder
    );

    if (option?.value === "product") {
      setIsProductSubSection(true);
    }

    // Update the selected type for the subsection
    clonedSubSections[subSectionIndex] = {
      type: option?.value || "",
      attrs: {}, // Add attrs for the selected type
      order: subSectionOrder || 1,
      isEditing: true,
    };

    clonedBlogInfo.sections[sectionIndex].body = clonedSubSections;

    validation.setFieldValue("body", clonedSubSections);
    // Update the blog info without affecting the title

    setBlogInfo((prevBlogInfo) => {
      const updatedSections = [...prevBlogInfo.sections]; // Clone the array
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex], // Preserve existing properties
        title: validation.values.title, // Keep title unchanged
        body: clonedSubSections, // Update only the required fields
      };

      return { ...prevBlogInfo, sections: updatedSections };
    });
  };

  const handleSaveSubSection = (
    sectionOrder: number,
    subSectionInfo: SubSection
  ) => {
    let clonedSubSections = [...validation.values.body];

    const clonedBlogInfo = { ...blogInfo };

    // find Section index
    const sectionIndex = clonedBlogInfo.sections.findIndex(
      (section_) => section_.order === section.order
    );

    // find the sub section index
    const subSectionIndex = clonedSubSections.findIndex(
      (subSection: SubSection) => subSection.order === subSectionInfo.order
    );

    clonedSubSections[subSectionIndex] = {
      ...subSectionInfo,
      isEditing: false, // Preserve `isEditing`
    };

    clonedBlogInfo.sections[sectionIndex].body = clonedSubSections;

    // update the formik state
    validation.setFieldValue("body", clonedSubSections);
    setBlogInfo(clonedBlogInfo);
  };

  const handleEditSubSection = (
    sectionOrder: number,
    subSection: SubSection
  ) => {
    const clonedBlogInfo = JSON.parse(JSON.stringify(blogInfo));
    let clonedSubSections = JSON.parse(JSON.stringify(validation.values.body));
    // let clonedSubSections = [...validation.values.body];
    // const clonedBlogInfo = { ...blogInfo };

    // find Section index
    const sectionIndex = clonedBlogInfo.sections.findIndex(
      (section_: Section) => section_.order === section.order
    );

    clonedBlogInfo.sections[sectionIndex].body = clonedSubSections;
    clonedBlogInfo.sections[sectionIndex].isEditing = true;

    // Find the sub-section index
    const subSectionIndex = clonedSubSections.findIndex(
      (subSections: SubSection) => subSections.order === subSection.order
    );

    if (subSectionIndex !== -1) {
      clonedSubSections[subSectionIndex] = {
        ...clonedSubSections[subSectionIndex],
        isEditing: true, // Set `isEditing` to true
      };
    }

    // Update the Formik state
    validation.setFieldValue("body", clonedSubSections);
    setBlogInfo(clonedBlogInfo);
  };

  const handleAddNewSection = () => {
    const newSection = {
      title: "",
      body: [
        {
          type: "",
          attrs: {},
          order: getNextOrder(section?.body || []),
          isEditing: true,
        },
      ],
      order: getNextOrder(blogValidation.values.sections),
      isEditing: true,
    };
    setSelectedSectionOrder(getNextOrder(blogValidation.values.sections));
    const updatedSections = [...blogValidation.values.sections, newSection];

    blogValidation.setFieldValue("sections", updatedSections); // Update Formik state
    setBlogInfo((prevBlogInfo: any) => ({
      ...prevBlogInfo, // Spread the previous state to preserve other properties
      sections: updatedSections, // Update the sections with the new list
    }));
  };

  const handleAddSubSection = () => {
    // const clonedSubSections = [...validation.values.body]; // Ensure it's an array
    // const clonedBlogInfo = { ...blogInfo };
    let clonedSubSections = JSON.parse(JSON.stringify(validation.values.body)); // Deep clone
    const clonedBlogInfo = JSON.parse(JSON.stringify(blogInfo));
    // find Section index
    const sectionIndex = clonedBlogInfo.sections.findIndex(
      (section_: Section) => section_.order === section.order
    );

    // Determine if last added subsection was a product
    const lastSubSection = clonedSubSections[clonedSubSections.length - 1];
    const isPrevProduct = lastSubSection?.type === "product";
    // setIsProductSubSection(isPrevProduct);
    let newSubSection;
    if (isPrevProduct) {
      newSubSection = {
        type: "product", // Default empty type
        attrs: { title: "", slug: "", image: "" }, // Initialize attrs as an empty object
        order: getNextOrder(clonedSubSections), // Generate next order number
        isEditing: true,
      };
    } else {
      newSubSection = {
        type: "", // Default empty type
        attrs: {}, // Initialize attrs as an empty object
        order: getNextOrder(clonedSubSections), // Generate next order number
        isEditing: true,
      };
    }

    clonedSubSections.push(newSubSection); // Add new sub-section
    clonedBlogInfo.sections[sectionIndex].body = clonedSubSections;
    clonedBlogInfo.sections[sectionIndex].isEditing = true;

    validation.setFieldValue("body", clonedSubSections); // Update Formik state
    setBlogInfo(clonedBlogInfo);
    setSelectedType("");
  };

  const handleEdit = (sectionOrder: number) => {
    setBlogInfo((prev) => {
      const updatedSections = prev.sections.map((section) =>
        section.order === sectionOrder
          ? { ...section, isEditing: true }
          : section
      );

      return { ...prev, sections: updatedSections };
    });
    blogValidation.setFieldValue("sections", blogInfo.sections);
  };

  const handleDeleteSubSection = (subsectionOrder: number) => {
    // let clonedSubSections = [...validation.values.body]; // Deep clone
    // const clonedBlogInfo = { ...blogInfo }; // Deep clone
    let clonedSubSections = JSON.parse(JSON.stringify(validation.values.body)); // Deep clone
    const clonedBlogInfo = JSON.parse(JSON.stringify(blogInfo)); // Deep clone
    // Find Section index
    const sectionIndex = clonedBlogInfo.sections.findIndex(
      (section_: Section) => section_.order === section.order
    );

    // Find the sub-section index
    const subSectionIndex = clonedSubSections.findIndex(
      (subSection: SubSection) => subSection.order === subsectionOrder
    );

    const prevSubSection = clonedSubSections[subSectionIndex - 1];
    if (prevSubSection?.type === "product") {
      setIsProductSubSection(true);
    } else {
      setIsProductSubSection(false);
    }
    if (subSectionIndex !== -1) {
      const deletedSubSection = clonedSubSections[subSectionIndex];

      // Store the _id of the deleted sub-section if it exists
      if (deletedSubSection._id) {
        setDeletedSubSectionIds([
          ...deletedSubSectionIds,
          deletedSubSection._id,
        ]);
        // setDeletedSubSectionIds((prev: string[]) => [...prev, deletedSubSection._id]);
      }
      clonedSubSections.splice(subSectionIndex, 1);
    }

    if (clonedSubSections.length === 0) {
      clonedSubSections = [
        {
          type: "",
          attrs: {},
          order: 1,
          isEditing: true,
        },
      ];
    }

    // Reassign order values to maintain correct sequence
    clonedSubSections = clonedSubSections.map(
      (subSection: SubSection, index: number) => ({
        ...subSection,
        order: index + 1,
      })
    );

    clonedBlogInfo.sections[sectionIndex].body = clonedSubSections;

    // Update Formik state
    validation.setFieldValue("body", clonedSubSections);
    setBlogInfo(clonedBlogInfo);
  };

  const handleDeleteSection = () => {
    let clonedBlogInfo = [...blogInfo.sections];

    // Find the index of the section to be deleted
    const sectionIndex = clonedBlogInfo.findIndex(
      (section_) => section_.order === section.order
    );
    // Remove the section
    if (sectionIndex !== -1) {
      const deletedSection = clonedBlogInfo[sectionIndex] as {
        _id?: string;
        body?: SubSection[];
      };

      // Collect all _id's from subsections of the deleted section (only existing ones)
      const deletedSubSectionIdsFromSection =
        deletedSection.body
          ?.map((subSection: any) => subSection._id)
          .filter(Boolean) || [];

      // Find previously deleted subsections related to this section
      const previouslyDeletedSubSections = deletedSubSectionIds.filter(
        (id) => deletedSubSectionIds.includes(id) // ✅ Check if ID was already marked for deletion
      );

      // Merge both lists of IDs
      const idsToRemove = [
        ...deletedSubSectionIdsFromSection,
        ...previouslyDeletedSubSections,
      ];

      // Remove these subsection IDs from deletedSubSectionIds state
      setDeletedSubSectionIds((prevIds) =>
        prevIds.filter((id) => !idsToRemove.includes(id))
      );

      // Update deletedSectionIds state
      if (deletedSection._id) {
        setDeletedSectionIds([...deletedSectionIds, deletedSection._id]);
      }

      // Remove the section
      clonedBlogInfo.splice(sectionIndex, 1);
    }

    // If no sections remain, reset with an empty section
    if (clonedBlogInfo.length === 0) {
      clonedBlogInfo = [
        {
          title: "",
          body: [
            { mediaId: "", type: "", attrs: {}, order: 1, isEditing: true },
          ],
          order: 1,
          isEditing: true,
        },
      ];
    }

    // Find the last section (highest order)
    const lastSection =
      clonedBlogInfo.length > 0
        ? clonedBlogInfo.reduce((prev, current) =>
            prev.order > current.order ? prev : current
          )
        : null;

    // Update the selected section order
    setSelectedSectionOrder(lastSection?.order || 1);

    // Update the blog state
    blogValidation.setFieldValue("sections", clonedBlogInfo);
    setBlogInfo((prev) => ({
      ...prev,
      sections: clonedBlogInfo,
    }));
  };

  const handleDeleteSectionModel = () => {
    handleDeleteSection();
    setDeleteSection(false);
  };

  const handleDeleteSubSectionModel = (subSectionOrder: number) => {
    handleDeleteSubSection(subSectionOrder);
    setDeleteSubSection(false);
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const updatedBody = Array.from(section.body);
    const [reorderedItem] = updatedBody.splice(result.source.index, 1);
    updatedBody.splice(result.destination.index, 0, reorderedItem);

    // Update the order property to maintain correct sorting
    updatedBody.forEach((item, index) => {
      item.order = index + 1;
    });
    setOrderUpdated(true);
    validation.setFieldValue("body", updatedBody);
  };

  const handleCancel = (sectionOrder: number) => {
    setBlogInfo((prev) => {
      const updatedSections = prev.sections.map((section) =>
        section.order === sectionOrder
          ? { ...section, isEditing: false } // Exit edit mode
          : section
      );

      return { ...prev, sections: updatedSections };
    });

    validation.resetForm(); // Reset Formik values
  };

  const handleUpdateStatus = () => {
    if (isBlogEditing) {
      const updatedBlogInfo = {
        ...blogInfo,
        status: "published" as BlogStatus,
      };
      setBlogInfo(updatedBlogInfo);
      blogValidation.setFieldValue("status", "published");
      handlePublishBlog();
    }
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    validation.handleSubmit();
    setOrderUpdated(false);
  };

  const subSectionIsEditing = section?.body?.some((sub) => sub.isEditing);

  return (
    <div>
      <DeleteModal
        show={deleteSection}
        onHide={() => setDeleteSection(false)}
        onDelete={handleDeleteSectionModel}
        message="Are you sure you want to delete this section?"
      />
      <Modal
        show={defaultModal}
        onHide={defaultToggle}
        id="defaultModal"
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[25rem] bg-white shadow rounded-md dark:bg-zink-600 flex flex-col h-full"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500"
        >
          <Modal.Title className="text-16">Unsaved Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <p className="text-slate-500 dark:text-zink-200">
            You have unsaved changes. Are you sure you want to cancel? Any
            unsaved progress will be lost.
          </p>
        </Modal.Body>
        <Modal.Footer className="flex items-center justify-center p-4 mt-auto border-t border-slate-200 dark:border-zink-500">
          <div className="flex justify-center gap-2 mt-6">
            <button
              className="bg-white text-slate-500 btn hover:text-slate-500 hover:bg-slate-100 focus:text-slate-500 focus:bg-slate-100 active:text-slate-500 active:bg-slate-100 dark:bg-zink-600 dark:hover:bg-slate-500/10 dark:focus:bg-slate-500/10 dark:active:bg-slate-500/10"
              onClick={(e) => {
                e.preventDefault();
                defaultToggle();
              }}
            >
              Cancel
            </button>
            <button
              id="deleteRecord"
              data-modal-close="deleteModal"
              className="text-white bg-red-500 border-red-500 btn hover:text-white hover:bg-red-600 hover:border-red-600 focus:text-white focus:bg-red-600 focus:border-red-600 focus:ring focus:ring-red-100 active:text-white active:bg-red-600 active:border-red-600 active:ring active:ring-red-100 dark:ring-custom-400/20"
              onClick={(e) => {
                e.preventDefault();
                blogValidation.resetForm();
                navigate("/blogs");
              }}
            >
              Discard Change
            </button>
          </div>
        </Modal.Footer>
      </Modal>
      <div className="mb-4">
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="">
            <div className="card grid p-5">
              <label
                htmlFor={`title`}
                className={`inline-block mb-2 text-base font-medium`}
              >
                Section Title
              </label>
              {section?.isEditing ? (
                <div>
                  <input
                    type="text"
                    id={`title`}
                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    placeholder="Section title"
                    name={`title`} // for title
                    onChange={validation.handleChange}
                    value={validation.values.title}
                  />
                  {validation.errors.title && validation.touched.title && (
                    <p className="mt-1 text-sm text-red-400">
                      {validation.errors.title}
                    </p>
                  )}
                </div>
              ) : (
                <span>{validation.values.title}</span>
              )}
            </div>
            <div className="text-md mb-5 font-bold">Sub Sections</div>
            <div>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {section?.body
                        ?.slice()
                        ?.sort(
                          (a: SubSection, b: SubSection) => a.order - b.order
                        )
                        .map((subsection: SubSection, subIndex: number) => {
                          const isPrevSubSectionProduct =
                            subIndex > 0 &&
                            validation.values.body?.[subIndex - 1]?.type ===
                              "product";
                          const isOnlyOneSubSection =
                            validation.values.body?.length === 1 &&
                            subIndex === 0;
                          const shouldShowTypeSelect =
                            subsection.isEditing &&
                            (!isPrevSubSectionProduct || isOnlyOneSubSection);
                          return (
                            <Draggable
                              key={subsection.order.toString()}
                              draggableId={`subSection-${subsection.order}`}
                              index={subIndex}
                            >
                              {(provided) => (
                                <div
                                  className="p-3 card"
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                >
                                  <div className="flex gap-x-2">
                                    <div {...provided.dragHandleProps}>
                                      <GripVertical color="#aeb2b7" />
                                    </div>
                                    <div className="grow w-[calc(100%-24px)]">
                                      {subsection.isEditing &&
                                        (!isPrevSubSectionProduct ||
                                          isOnlyOneSubSection) && (
                                          <div>
                                            <label
                                              htmlFor={`sectionTitle`}
                                              className="inline-block mb-2 text-base font-medium"
                                            >
                                              Sub Section Type
                                            </label>
                                            <Select
                                              className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                              options={typeOptions}
                                              value={typeOptions.find(
                                                (option) =>
                                                  option.value ===
                                                  subsection.type
                                              )}
                                              defaultValue={typeOptions.find(
                                                (option) =>
                                                  option.value ===
                                                  subsection.type
                                              )}
                                              onChange={(option: any) => {
                                                handleTypeChange(
                                                  option,
                                                  subsection.order
                                                );
                                              }}
                                            />
                                            {validation.errors.body?.[subIndex]
                                              ?.type &&
                                              validation.touched?.body?.[
                                                subIndex
                                              ]?.type && (
                                                <div className="text-red-500 text-sm pt-2">
                                                  {
                                                    validation.errors.body[
                                                      subIndex
                                                    ].type
                                                  }
                                                </div>
                                              )}
                                          </div>
                                        )}
                                      {subsection.type === "product" && (
                                        <Product
                                          sectionOrder={section.order}
                                          subSectionOrder={subsection.order}
                                          handleSaveSubSection={
                                            handleSaveSubSection
                                          }
                                          subSection={subsection}
                                          handleEditSubSection={
                                            handleEditSubSection
                                          }
                                          handleDeleteSubSection={
                                            handleDeleteSubSection
                                          }
                                        />
                                      )}
                                      {subsection.type === "paragraph" && (
                                        <Paragraph
                                          sectionOrder={section.order}
                                          subSectionOrder={subsection.order}
                                          handleSaveSubSection={
                                            handleSaveSubSection
                                          }
                                          subSection={subsection}
                                          handleEditSubSection={
                                            handleEditSubSection
                                          }
                                          handleDeleteSubSection={
                                            handleDeleteSubSection
                                          }
                                          selectedType={selectedType}
                                          setSelectedType={setSelectedType}
                                        />
                                      )}
                                      {subsection.type === "quote" && (
                                        <Quote
                                          sectionOrder={section.order}
                                          subSectionOrder={subsection.order}
                                          handleSaveSubSection={
                                            handleSaveSubSection
                                          }
                                          subSection={subsection}
                                          handleEditSubSection={
                                            handleEditSubSection
                                          }
                                          handleDeleteSubSection={
                                            handleDeleteSubSection
                                          }
                                          section={section}
                                        />
                                      )}
                                      {subsection.type === "list" && (
                                        <List
                                          sectionOrder={section.order}
                                          subSectionOrder={subsection.order}
                                          handleSaveSubSection={
                                            handleSaveSubSection
                                          }
                                          subSection={subsection}
                                          handleEditSubSection={
                                            handleEditSubSection
                                          }
                                          handleDeleteSubSection={
                                            handleDeleteSubSection
                                          }
                                        />
                                      )}
                                      {subsection.type === "embed" && (
                                        <Embed
                                          sectionOrder={section.order}
                                          subSectionOrder={subsection.order}
                                          handleSaveSubSection={
                                            handleSaveSubSection
                                          }
                                          subSection={subsection}
                                          handleEditSubSection={
                                            handleEditSubSection
                                          }
                                          handleDeleteSubSection={
                                            handleDeleteSubSection
                                          }
                                        />
                                      )}
                                      {subsection.type === "table" && (
                                        <Table
                                          sectionOrder={section.order}
                                          subSectionOrder={subsection.order}
                                          handleSaveSubSection={
                                            handleSaveSubSection
                                          }
                                          subSection={subsection}
                                          handleEditSubSection={
                                            handleEditSubSection
                                          }
                                          handleDeleteSubSection={
                                            handleDeleteSubSection
                                          }
                                        />
                                      )}
                                      {subsection.type === "image" && (
                                        <Imaegs
                                          sectionOrder={section.order}
                                          subSectionOrder={subsection.order}
                                          handleSaveSubSection={
                                            handleSaveSubSection
                                          }
                                          subSection={subsection}
                                          handleEditSubSection={
                                            handleEditSubSection
                                          }
                                          handleDeleteSubSection={
                                            handleDeleteSubSection
                                          }
                                        />
                                      )}
                                      {subsection.type === "video" && (
                                        <Video
                                          sectionOrder={section.order}
                                          subSectionOrder={subsection.order}
                                          handleSaveSubSection={
                                            handleSaveSubSection
                                          }
                                          subSection={subsection}
                                          handleEditSubSection={
                                            handleEditSubSection
                                          }
                                          handleDeleteSubSection={
                                            handleDeleteSubSection
                                          }
                                        />
                                      )}
                                      {subsection.type === "code" && (
                                        <Code
                                          sectionOrder={section.order}
                                          subSectionOrder={subsection.order}
                                          handleSaveSubSection={
                                            handleSaveSubSection
                                          }
                                          subSection={subsection}
                                          handleEditSubSection={
                                            handleEditSubSection
                                          }
                                          handleDeleteSubSection={
                                            handleDeleteSubSection
                                          }
                                        />
                                      )}
                                    </div>
                                  </div>
                                  {!subsection.type && (
                                    <div className="flex justify-end pt-5">
                                      <button
                                        type="button"
                                        className="text-red-500"
                                        onClick={() =>
                                          setDeleteSubSection(true)
                                        }
                                      >
                                        <div className="flex align-center">
                                          <Trash2 className="size-4 text-red-500"></Trash2>
                                          <span className="ml-1">Delete</span>
                                        </div>
                                      </button>
                                      <DeleteModal
                                        show={deleteSubSection}
                                        onHide={() =>
                                          setDeleteSubSection(false)
                                        }
                                        onDelete={() =>
                                          handleDeleteSubSectionModel(
                                            subsection.order
                                          )
                                        }
                                        message="Are you sure you want to remove this subsection?"
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <div className="flex gap-x-2 pb-5 justify-end">
                <button
                  type="button"
                  className={`text-custom-500 ${
                    isLoading
                      ? "cursor-not-allowed opacity-50"
                      : "hover:bg-custom-200"
                  } btn bg-custom-100 focus:text-white focus:bg-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:ring active:ring-custom-100 dark:bg-custom-500/20 dark:text-custom-500 dark:hover:bg-custom-500 dark:hover:text-white dark:focus:bg-custom-500 dark:focus:text-white dark:active:bg-custom-500 dark:active:text-white dark:ring-custom-400/20`}
                  onClick={() => handleAddSubSection()}
                  disabled={isLoading}
                >
                  <div className="flex">
                    <Plus size={18} />
                    <span>
                      {isProductSubSection ? "Add Product" : "Add Sub Section"}
                    </span>
                  </div>
                </button>
              </div>

              <hr className="border-[#cdcdcd]" />
              <div className="pt-5 flex gap-x-3 justify-end">
                {section?.isEditing || orderUpdated ? (
                  <div className="flex gap-x-3">
                    {section.title !== "" && (
                      <button
                        type="button"
                        className={`${
                          isLoading ? "cursor-not-allowed opacity-50" : ""
                        } text-red-500`}
                        onClick={() => handleCancel(section.order)}
                        disabled={isLoading}
                      >
                        <div className="flex align-center">
                          <span className="ml-1">Cancel</span>
                        </div>
                      </button>
                    )}
                    <div>
                      <button
                        data-tooltip-id="default"
                        type="button"
                        className={`${
                          subSectionIsEditing || (isLoading && !orderUpdated)
                            ? "opacity-50 cursor-not-allowed btn text-green-500 bg-green-100 hover:bg-green-100 hover:text-green-600"
                            : "text-green-500 bg-green-100 btn hover:text-white hover:bg-green-600 focus:text-white focus:bg-green-600 focus:ring focus:ring-green-100 active:text-white active:bg-green-600 active:ring active:ring-green-100 dark:bg-green-500/20 dark:text-green-400 dark:hover:bg-green-500 dark:hover:text-white dark:focus:bg-green-500 dark:focus:text-white dark:active:bg-green-500 dark:active:text-white dark:ring-green-400/20"
                        }`}
                        onClick={(e) => handleSubmit(e)}
                        disabled={
                          subSectionIsEditing || (isLoading && !orderUpdated)
                        }
                      >
                        <div className="flex align-center">
                          <Save className="size-4"></Save>
                          <span className="ml-1">Save Section</span>
                        </div>
                      </button>
                      {subSectionIsEditing && (
                        <Tooltip
                          id="default"
                          place="top"
                          content="Subsection must be saved before saving the section"
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={`${
                      isLoading
                        ? "cursor-not-allowed opacity-50 text-custom-500 hover:text-custom-500"
                        : "hover:bg-custom-600 text-custom-500 hover:text-white"
                    } btn bg-custom-100 focus:text-white focus:bg-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:ring active:ring-custom-100 dark:bg-custom-500/20 dark:text-custom-500 dark:hover:bg-custom-500 dark:hover:text-white dark:focus:bg-custom-500 dark:focus:text-white dark:active:bg-custom-500 dark:active:text-white dark:ring-custom-400/20`}
                    onClick={() => {
                      handleEdit(section.order);
                    }}
                    disabled={isLoading}
                  >
                    <div className="flex align-center">
                      <FileEdit className="size-4"></FileEdit>
                      <span className="ml-1">Edit</span>
                    </div>
                  </button>
                )}
                {!section?.isEditing && !orderUpdated && (
                  <button
                    type="button"
                    className={`text-custom-500 ${
                      isLoading
                        ? "cursor-not-allowed opacity-50"
                        : "hover:bg-custom-200"
                    } btn bg-custom-100 focus:text-white focus:bg-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:ring active:ring-custom-100 dark:bg-custom-500/20 dark:text-custom-500 dark:hover:bg-custom-500 dark:hover:text-white dark:focus:bg-custom-500 dark:focus:text-white dark:active:bg-custom-500 dark:active:text-white dark:ring-custom-400/20`}
                    onClick={() => handleAddNewSection()}
                    disabled={isLoading}
                  >
                    <div className="flex">
                      <Plus size={18} />
                      <span>Add Section</span>
                    </div>
                  </button>
                )}
                <button
                  type="button"
                  className={`${
                    isLoading ? "cursor-not-allowed opacity-50" : ""
                  } text-red-500`}
                  onClick={() => setDeleteSection(true)}
                  disabled={isLoading}
                >
                  <div className="flex align-center">
                    <Trash2 className="size-4 text-red-500"></Trash2>
                    <span className="ml-1">Delete</span>
                  </div>
                </button>
              </div>
            </div>
            <div className="flex justify-end pt-5">
              {isBlogEditing ? (
                <div className="flex gap-x-3">
                  <button
                    type="button"
                    className={`${
                      isLoading ? "cursor-not-allowed opacity-50" : ""
                    } text-red-500`}
                    onClick={() => {
                      // blogValidation.resetForm();
                      // navigate("/blogs");
                      defaultToggle();
                    }}
                    disabled={isLoading}
                  >
                    <div className="flex align-center">
                      <span className="ml-1">Cancel</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    className={`text-white ${
                      isLoading || section?.isEditing
                        ? "opacity-50 cursor-not-allowed pointer-events-none"
                        : ""
                    } btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20`}
                    // onClick={() => setDeleteSection(true)}
                    disabled={isLoading || section?.isEditing}
                    onClick={(e) => {
                      e.preventDefault();
                      handlePublishBlog();
                      // navigate("/blogs");
                    }}
                  >
                    <div className="flex align-center">
                      {isLoading ||
                        (section?.isEditing && (
                          <Loader2 className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                        ))}
                      <span className="ml-1">Save Changes</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    className={`text-white ${
                      isLoading || section?.isEditing
                        ? "opacity-50 cursor-not-allowed pointer-events-none"
                        : ""
                    } btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20`}
                    disabled={isLoading}
                    onClick={(e) => {
                      e.preventDefault();
                      // handlePublishBlog();
                      handleUpdateStatus();
                      // navigate("/blogs");
                    }}
                  >
                    <div className="flex align-center">
                      {isLoading && (
                        <Loader2 className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                      )}
                      <span className="ml-1">Publish Blog</span>
                    </div>
                  </button>
                </div>
              ) : (
                <Dropdown className="relative">
                  <div className="flex items-center">
                    <button
                      type="button"
                      className={`text-white  ${
                        isLoading
                          ? "opacity-50 cursor-not-allowed pointer-events-none"
                          : ""
                      } flex dropdown-toggle btn
                    bg-custom-500 border-custom-500 ltr:rounded-r-none rtl:rounded-l-none
                    focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100
                    active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100
                    dark:ring-custom-400/20`}
                      disabled={isLoading}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePublishBlog();
                        // navigate("/blogs");
                      }}
                    >
                      {isLoading && (
                        <Loader2 className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                      )}
                      Publish Blog
                    </button>
                    <Dropdown.Trigger
                      type="a"
                      id="dropdownSplit1"
                      data-bs-toggle="dropdown"
                      className="dropdown-toggle ltr:rounded-l-none rtl:rounded-r-none flex items-center justify-center size-[37.5px] p-0 text-white btn bg-custom-600 border-custom-600 hover:text-white hover:bg-custom-700 hover:border-custom-700 focus:text-white focus:bg-custom-700 focus:border-custom-700 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-700 active:border-custom-700 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    >
                      <ChevronDown className="inline-block size-4"></ChevronDown>
                    </Dropdown.Trigger>
                  </div>
                  <Dropdown.Content
                    placement="right-end"
                    as="ul"
                    className="absolute z-50 py-2 mt-1 list-none bg-white rounded-md shadow-md ltr:text-left rtl:text-right dropdown-menu min-w-max dark:bg-zink-600"
                    aria-labelledby="dropdownSplit1"
                  >
                    <li>
                      <a
                        className="block px-4 py-1.5 text-base font-medium transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200"
                        href="#!"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePublishBlog();
                          // navigate("/blogs");
                        }}
                      >
                        Publish
                      </a>
                    </li>
                    <li>
                      <a
                        className="block px-4 py-1.5 text-base font-medium transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200"
                        href="#!"
                      >
                        Draft
                      </a>
                    </li>
                  </Dropdown.Content>
                </Dropdown>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SectionInfo;
